import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CheckBox,
  Typography
} from "@dealer-center/core-components";
import type { PermissionGroup as PermissionGroupModel } from "./groupPermissions";

export interface PermissionGroupProps {
  group: PermissionGroupModel;
}

/**
 * One group of permission rows: a heading, and its rows as non-interactive checked/unchecked
 * controls (FR-032, FR-033, FR-036).
 *
 * **Granted state is not colour-only** (FR-050). Each row is a real `CheckBox` with `checked` and
 * `disabled`, so the state is a programmatic property a screen reader announces — not a class name
 * or a tick glyph. `disabled` (rather than a no-op handler) is what conveys "unavailable" to
 * assistive technology, which FR-052's "MUST NOT masquerade as a working one" requires.
 *
 * **Rendered as an `Accordion` so the group is collapsible and conveys its expanded state** (T034).
 * Every group is collapsible, not just the catch-all: the catch-all is the one that *needs* it —
 * ~128 of 186 rows land there for a real account, and an undifferentiated wall of code-only rows
 * buries the meaningful ones — but making named groups behave the same way avoids a second heading
 * treatment for no benefit.
 *
 * **Expanded by default**, including the catch-all. SC-007 asks for "a complete, legible, navigable
 * list — no truncation", and collapsing 128 of 186 rows on load is truncation by default. MUI also
 * *unmounts* a collapsed panel's children, so collapsed-by-default would put the majority of rows
 * outside the DOM entirely — invisible to find-in-page and to a screen reader alike.
 *
 * **Deliberately not wrapped in Interstate's `AccordionGroup`.** That component coordinates
 * expansion by `cloneElement`-ing its **direct** children with an injected `expanded` /
 * `defaultExpanded` prop. A wrapper component in between — this one — receives those props and does
 * not forward them, so every group silently rendered collapsed. Since each group here expands
 * independently anyway, `defaultExpanded` on the `Accordion` is both simpler and correct.
 *
 * The heading is MUI's own: `Accordion` wraps its summary in an `<h3 class="MuiAccordion-heading">`,
 * giving the `<h3><button aria-expanded>` nesting the ARIA disclosure pattern calls for and the real
 * heading element T034 asks for. Adding a second `Typography tag="h3"` inside would emit two
 * headings per group, so the label renders as a plain `span`.
 */
export const PermissionGroup = ({ group }: PermissionGroupProps) => {
  const testIdSuffix = group.isCatchAll ? "catch-all" : group.key;

  return (
    <Accordion defaultExpanded data-testid={`permission-group-${testIdSuffix}`}>
      <AccordionSummary
        data-testid={`permission-group-summary-${testIdSuffix}`}
      >
        <Box
          sx={{ alignItems: "baseline", columnGap: "10px", display: "flex" }}
        >
          <Typography tag={"span"} variant={"strong-md"}>
            {group.label}
          </Typography>
          {/*
           * The row count, per the prototype's `.count`. Decorative, hence `aria-hidden`: the rows
           * are a list of labelled controls a screen reader already enumerates, so spoken aloud this
           * is a bare number ahead of the thing it counts. Its value is to a sighted user scanning
           * collapsed groups for the big one — which, for a real account, is the catch-all holding
           * ~128 of 186 rows.
           *
           * Not a label key: a numeral is data, like the permission names beside it, not copy.
           */}
          <Typography
            aria-hidden={"true"}
            color={"sem.color.on-surface.muted"}
            data-testid={`permission-group-count-${testIdSuffix}`}
            tag={"span"}
            variant={"body-xs"}
          >
            {group.rows.length}
          </Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        {/*
         * One column, not two. A permission is a checkbox with a prose label of unpredictable length
         * — from "Powermail" to a raw `SmartSites.PageBuilder` code — so a two-column grid gives
         * every row the width of the longest label in the group and leaves ragged gaps beside the
         * short ones. Reading order also stops being obvious: with two columns the eye has to decide
         * whether the list runs across or down. The prototype's single column is the right call for a
         * scannable list of on/off rows.
         */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            rowGap: "4px"
          }}
        >
          {group.rows.map(row => (
            <CheckBox
              disabled
              checked={row.isGranted}
              data-testid={`permission-row-${row.code}`}
              id={`permission-${row.code}`}
              key={row.code}
              label={row.label}
              name={row.code}
              value={row.code}
            />
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
